import { AgentStateData, AgentTodayData, KeyValuePair, WorkItem, WorkItemKpis } from '../models/reports.external';
import { AgentStates, IAgentReport, IHandledWorkItem, MediaType, MediaTypes, PartialReport, WorkItemHandlingStats, defaultWorkItemHandlingStats } from '../models/reports.internal';
import { getTimestamp } from '../utils/timeUtils';
import getWorkItemInfoFromMainConversation from './getWorkItemInfoFromMainConversation';

const reduceToObject = <TValue, TRes>(arr: KeyValuePair<TValue>[], selector: (value: TValue) => unknown = ((v) => v)): Record<string, TRes> =>
  arr.reduce((coll, { key, value }) => ({ ...coll, [key]: selector(value) }), {});

const getWorkItemHandlingStats = (kpis: KeyValuePair<WorkItemKpis>[], mediaType: MediaType): WorkItemHandlingStats => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const kpi = kpis.find(({ key }) => key === mediaType)!.value;
  return {
    handled: kpi.handledCount,
    missed: kpi.missedCount,
    avgHandlingTime: kpi.averageHandlingTimeInSeconds,
  };
};

export function processAgentToday(agentToday: AgentTodayData): PartialReport<IAgentReport> {
  return {
    id: agentToday.id,
    ...(agentToday.name ? { name: agentToday.name } : {}),
    tenantId: agentToday.tenantId,
    timeSpentInStates: {
      ...(AgentStates.reduce((coll, key) => ({ ...coll, [key]: 0 }), {} as IAgentReport['timeSpentInStates'])),
      ...reduceToObject(agentToday.secondsSpentPerState),
    },
    timeSpentInBreakNames: reduceToObject(agentToday.secondsSpentPerBreakName),
    workItemsByMedia: MediaTypes.reduce((coll, media) => ({
      ...coll,
      [media]: agentToday.workItemKpisPerMediaType.find(({ key }) => key === media)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ? getWorkItemHandlingStats(agentToday.workItemKpisPerMediaType, media)
        : defaultWorkItemHandlingStats(),
    }), {} as IAgentReport['workItemsByMedia']),
    workItemsByDispositionCode: reduceToObject(agentToday.workItemKpisPerDispositionCode, val => val.handledCount),
    loggedInTimestamp: agentToday.loggedInAt ? getTimestamp(agentToday.loggedInAt) : null,
    loggedOutTimestamp: agentToday.loggedOutAt ? getTimestamp(agentToday.loggedOutAt) : null,
  };
}

export function processAgentState(agentState: AgentStateData): PartialReport<IAgentReport> {
  return {
    id: agentState.agentId,
    ...(agentState.agentName ? { name: agentState.agentName } : {}),
    currentState: agentState.state,
    currentStateSince: getTimestamp(agentState.stateSince),
    breakName: agentState.breakName,
  };
}

export function processWorkItem(workItem: WorkItem): IHandledWorkItem {
  const mainConversationInfo = getWorkItemInfoFromMainConversation(workItem);

  return {
    primaryConversationId: mainConversationInfo.conversationId,
    channelType: mainConversationInfo.channelType,
    virtualConversationType: mainConversationInfo.virtualConversationType,
    createdAtTimestamp: getTimestamp(workItem.createdAt),
    customerName: mainConversationInfo.customerName,
    customerIdentifier: mainConversationInfo.customerIdentifier,
    state: workItem.status,
    channelName: mainConversationInfo.channelName,
    isOutbound: mainConversationInfo.isOutbound ?? null,
  };
}